import { useCallback, useRef } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";

const I18N_BASE_URL = "https://i18n.baback.co";
// Keys can belong to the legacy widget (apps/return-widget), the new widget
// v2 (return-widget-v3), or both — see widget-keys.generated.ts. Both
// namespaces are fetched and merged so search and default-text lookups work
// regardless of which generation a key belongs to.
const I18N_NAMESPACES = ["return-widget", "return-widget-v3"] as const;

type Translations = Record<string, string>;

async function fetchNamespaceDict(namespace: string, lang: string): Promise<Translations> {
  const response = await fetch(`${I18N_BASE_URL}/${namespace}/${lang}.json`);
  if (!response.ok) return {};
  return response.json();
}

async function fetchMergedDict(lang: string): Promise<Translations> {
  const dicts = await Promise.all(
    I18N_NAMESPACES.map((namespace) => fetchNamespaceDict(namespace, lang)),
  );
  return Object.assign({}, ...dicts);
}

function get(dict: Translations | undefined, key: string): string | null {
  if (!dict) return null;
  if (typeof dict[key] === "string") return dict[key];

  // Translation files use nested dicts when keys are dotted. Walk the tree.
  const parts = key.split(".");
  let cursor: unknown = dict;
  for (const part of parts) {
    if (cursor == null || typeof cursor !== "object") return null;
    cursor = (cursor as Record<string, unknown>)[part];
  }
  return typeof cursor === "string" ? cursor : null;
}

export function useTranslationDefaults(lang: string) {
  return useQuery({
    queryKey: ["widget-defaults", lang],
    queryFn: () => fetchMergedDict(lang),
    staleTime: 1000 * 60 * 60,
    retry: false,
  });
}

/**
 * Returns a lookup function that resolves the default translation string for a
 * given key in the loaded dictionary. Useful for displaying the "default"
 * column in the wording page.
 */
export function useTranslationDefaultResolver(lang: string) {
  const { data, isLoading } = useTranslationDefaults(lang);
  return {
    isLoading,
    resolve: (key: string) => get(data, key),
  };
}

/**
 * Loads translations for multiple language codes and returns a function that
 * checks whether a given key's default text matches a query string in any of
 * the loaded languages.
 */
export function useTranslationMultiLangSearch(langs: string[]) {
  const results = useQueries({
    queries: langs.map((lang) => ({
      queryKey: ["widget-defaults", lang],
      queryFn: () => fetchMergedDict(lang),
      staleTime: 1000 * 60 * 60,
      retry: false,
    })),
  });

  // Keep a ref so callbacks always read the latest results without being
  // recreated on every render (avoids invalidating useMemo deps unnecessarily).
  const resultsRef = useRef(results);
  resultsRef.current = results;

  // True once every language fetch has either succeeded or errored.
  const isLoaded = results.every((r) => r.isSuccess);

  const matchesAny = useCallback(
    (key: string, normalizedQuery: string): boolean => {
      if (!normalizedQuery) return true;
      return resultsRef.current.some((r) => {
        const val = get(r.data, key);
        return val != null && val.toLowerCase().includes(normalizedQuery);
      });
    },
    [], // stable reference; reads latest data via ref
  );

  const resolveForLang = useCallback(
    (key: string, langCode: string): string | null => {
      const idx = langs.indexOf(langCode);
      if (idx === -1) return null;
      return get(resultsRef.current[idx]?.data, key);
    },
    [langs],
  );

  return { matchesAny, resolveForLang, isLoaded };
}
